import { useCallback, useEffect, useState } from 'react'
import { Copy, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { employeeManualOrderApi, type ManualOrderTracking } from '@/api/employee-manual-order'

interface ExpressTrackingTimelineProps {
  orderId: number
  scope?: string
  /** 展示用快递公司名（来自订单 delivery_name，查询失败/无结果时兜底显示） */
  comName?: string
  /** 展示用快递单号（来自订单 delivery_id，查询失败/无结果时兜底显示） */
  nu?: string
}

// 物流状态文案 → 徽章配色（快递100 state）
function getStateBadgeClass(state: string): string {
  if (state.includes('签收')) return 'border-green-200 bg-green-50 text-green-700'
  if (state.includes('派') || state.includes('在途') || state.includes('运输') || state.includes('揽收')) {
    return 'border-blue-200 bg-blue-50 text-blue-700'
  }
  if (state.includes('疑难') || state.includes('退') || state.includes('拒签')) {
    return 'border-red-200 bg-red-50 text-red-700'
  }
  return 'bg-muted text-muted-foreground'
}

/**
 * 物流轨迹时间线：打开即查询，展示单号/当前状态/轨迹列表
 */
export function ExpressTrackingTimeline({ orderId, scope, comName, nu: fallbackNu }: ExpressTrackingTimelineProps) {
  const [tracking, setTracking] = useState<ManualOrderTracking | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTracking = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await employeeManualOrderApi.getTracking(orderId, scope)
      setTracking(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : '查询物流失败')
    } finally {
      setIsLoading(false)
    }
  }, [orderId, scope])

  useEffect(() => {
    void fetchTracking()
  }, [fetchTracking])

  // 查询结果缺失时兜底显示订单上的快递公司/单号
  const com = tracking?.com || comName || '物流查询'
  const nu = tracking?.nu || fallbackNu

  const handleCopyNu = async () => {
    if (!nu) return
    try {
      await navigator.clipboard.writeText(nu)
      toast.success('快递单号已复制')
    } catch {
      toast.error('复制失败')
    }
  }

  return (
    <div className="space-y-4">
      {/* 单号 + 当前状态 */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{com}</span>
        {nu && (
          <button
            type="button"
            onClick={handleCopyNu}
            className="inline-flex items-center gap-1 rounded border bg-muted/40 px-1.5 py-0.5 font-mono text-xs hover:bg-muted"
            title="复制单号"
          >
            {nu}
            <Copy className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
        {tracking?.state && (
          <Badge variant="outline" className={getStateBadgeClass(tracking.state)}>
            {tracking.state}
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((index) => (
            <div key={index} className="flex gap-3">
              <Skeleton className="mt-1 h-3 w-3 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button size="sm" variant="outline" onClick={() => void fetchTracking()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            重试
          </Button>
        </div>
      ) : tracking && tracking.timeline.length > 0 ? (
        <div className="relative">
          <div className="absolute bottom-2 left-[5px] top-2 w-px bg-border" />
          <div className="space-y-4">
            {tracking.timeline.map((event, index) => (
              <div key={`${event.time}-${index}`} className="relative flex gap-3">
                <div
                  className={`z-10 mt-1 h-3 w-3 rounded-full ${
                    index === 0 ? 'bg-primary ring-4 ring-primary/15' : 'bg-muted-foreground/40'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${index === 0 ? 'font-medium' : 'text-muted-foreground'}`}>
                    {event.context}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{event.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="py-6 text-center text-sm text-muted-foreground">暂无物流信息</div>
      )}
    </div>
  )
}
